// ============================================================================
// Voice capture + AI parsing.
//
// Uses the Web Speech API (webkitSpeechRecognition / SpeechRecognition) for
// transcription, then POSTs the transcript to the Anthropic Cloud Function
// proxy with a strict "respond with JSON only" instruction so we get back
// structured fields (item, quantity, unit, facility, urgency, notes).
//
// Browser support: Chrome on Android, Safari on iOS 15+, desktop Chrome.
// Firefox does NOT support webkitSpeechRecognition. We feature-detect and
// hide the mic if unsupported — the form fallback covers that case.
//
// Cloud Function contract assumed (anthropicProxy):
//   POST <VITE_ANTHROPIC_PROXY_URL>
//   Authorization: Bearer <Firebase ID token>   (signed-in staff only, item #907)
//   { messages: [{role:'user', content: string}], system?: string }
//   The model and the size are fixed on the server.
//   -> Anthropic Messages API response { content: [{ type:'text', text }] }
// We defensively unwrap because if the proxy reshapes the body it tends to
// flatten to { text } or { message }.
// ============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  Facility,
  ParsedSupplyBatch,
  ParsedSupplyItem,
  Supply,
  SupplyUrgency,
} from '../../shared/types';
import { errorMessage } from '../../shared/errors';
import { firebaseAuth } from '../../shared/firebase';

// ---------------------------------------------------------------------------
// Types for the non-standard Web Speech API surface
// ---------------------------------------------------------------------------

interface SpeechResultAlt {
  transcript: string;
}
interface SpeechResult {
  0: SpeechResultAlt;
  isFinal: boolean;
}
interface SpeechResults {
  [index: number]: SpeechResult;
  length: number;
}
interface SpeechRecognitionEvent {
  results: SpeechResults;
}
interface SpeechRecognitionErrorEvent {
  error: string;
}
interface SpeechRecognitionInstance {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((e: SpeechRecognitionEvent) => void) | null;
  onerror: ((e: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
}
type SpeechRecognitionCtor = new () => SpeechRecognitionInstance;

function getSpeechRecognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any;
  return (w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null) as
    | SpeechRecognitionCtor
    | null;
}

export function isVoiceSupported(): boolean {
  return getSpeechRecognitionCtor() !== null;
}

// ---------------------------------------------------------------------------
// useVoiceRecognition hook
// ---------------------------------------------------------------------------

export function useVoiceRecognition() {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState<string | null>(null);
  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);

  const stop = useCallback(() => {
    recognitionRef.current?.stop();
  }, []);

  const start = useCallback(() => {
    setError(null);
    setTranscript('');
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) {
      setError('Voice input is not supported in this browser. Use the form below.');
      return;
    }
    const rec = new Ctor();
    rec.continuous = false;
    rec.interimResults = false;
    rec.lang = 'en-US';
    rec.onresult = (e) => {
      // Concatenate all final results — usually just one.
      const parts: string[] = [];
      for (let i = 0; i < e.results.length; i++) {
        parts.push(e.results[i][0].transcript);
      }
      setTranscript(parts.join(' ').trim());
    };
    rec.onerror = (e) => {
      // 'no-speech' / 'aborted' aren't really errors — silence the noise.
      if (e.error !== 'aborted' && e.error !== 'no-speech') {
        setError(`Voice input error: ${e.error}`);
      }
    };
    rec.onend = () => {
      setIsListening(false);
    };
    recognitionRef.current = rec;
    try {
      rec.start();
      setIsListening(true);
    } catch (e) {
      // start() throws if already running — surface a friendly message.
      setError(errorMessage(e, 'Could not start voice input.'));
    }
  }, []);

  useEffect(() => {
    return () => recognitionRef.current?.abort();
  }, []);

  return { isListening, transcript, error, start, stop, setTranscript };
}

// ---------------------------------------------------------------------------
// AI parser — calls the anthropicProxy with a strict JSON-only instruction.
// ---------------------------------------------------------------------------

const PROXY_URL = import.meta.env.VITE_ANTHROPIC_PROXY_URL;

const SYSTEM_PROMPT = `You are a parser for janitorial supply requests at Terminal Cleaning Services.
You receive a single voice transcript that may describe MULTIPLE supply line items
for one facility, plus optional context like "by Friday" or "ASAP". Respond with
ONLY a JSON object — no prose, no markdown, no code fence. The JSON has exactly
this shape:

{
  "header": {
    "facility":    string | null,   // facility name as the user said it
    "facility_id": string | null,   // best-match FAC-XXXX from the facility list
    "urgency":     "Emergency" | "Soon" | "Planned" | null,
    "date_needed": string | null,   // ISO YYYY-MM-DD, computed from phrases like "by Friday"
    "notes":       string | null    // anything else worth keeping
  },
  "items": [
    {
      "item":      string | null,   // the item the user named
      "quantity":  number | null,   // 1 when phrasing implies one ("a case of"); null for "some" / "a few"
      "unit":      string | null,   // CASE | EACH | BOX | BOTTLE | ROLL (uppercase)
      "supply_id": string | null    // best-match SUP-XXXX from the catalog list, null when uncertain
    }
  ]
}

Rules:
  * Split each item the user names into its own entry in "items". A typical
    transcript is "I need a case of X, a case of Y, some Z for FACILITY by FRIDAY"
    → three items in the array.
  * Quantity / unit per item:
      - "a case of X" / "one case of X"  → quantity 1, unit CASE
      - "two cases of X"                  → quantity 2, unit CASE
      - "a bottle of X"                   → quantity 1, unit BOTTLE
      - "some X" / "a few X" / "X"        → quantity null, unit null  (ambiguous — let the AM fill it in)
  * Urgency lives ONLY on the header (one urgency for the whole batch):
      - "urgent" / "ASAP" / "emergency" / "right now"   → Emergency
      - "soon" / "this week" / "by friday" / "by tuesday"→ Soon
      - "planned" / "next month" / "no rush"            → Planned
      - default to null if no signal is present.
  * "by <weekday>" → set date_needed to the NEXT occurrence of that weekday on
    or after today (today's date is included in the user message as "Today: YYYY-MM-DD").
    Use that anchor to compute the absolute date. If no weekday/date phrase is
    present, leave date_needed null.
  * Only assign supply_id / facility_id when you are confident the match is
    correct. Otherwise leave null and let the AM resolve it in the UI.
  * Match facilities loosely — "KCUC" should match a facility named
    "KCUC – University Health Surgery Center"; "DaVita 135th" should match
    "DaVita Dialysis – 135th Street".
  * If the transcript names no items at all, return "items": [] and put the
    full transcript in header.notes so the UI can surface it.`;

export interface ParserContext {
  supplies: Pick<Supply, 'supply_id' | 'item_name' | 'supply_category'>[];
  facilities: Pick<Facility, 'facility_id' | 'facility_name'>[];
}

/**
 * Parse a free-form transcript into a structured multi-item batch. Returns
 * null on any proxy / network failure so the caller can fall back to the form.
 */
export async function parseTranscript(
  transcript: string,
  ctx: ParserContext,
): Promise<ParsedSupplyBatch | null> {
  if (!PROXY_URL) {
    console.warn('[voice] VITE_ANTHROPIC_PROXY_URL not set; skipping parse');
    return null;
  }
  if (!transcript.trim()) return null;

  // Trim the catalog/facility lists so the prompt fits in a small token
  // budget — items have ID + name; facilities have ID + name. The model
  // doesn't need pricing or RAG to do the match.
  const supplyList = ctx.supplies
    .filter((s) => s.item_name)
    .map((s) => `${s.supply_id} | ${s.supply_category ?? ''} | ${s.item_name}`)
    .join('\n');
  const facilityList = ctx.facilities
    .filter((f) => f.facility_name)
    .map((f) => `${f.facility_id} | ${f.facility_name}`)
    .join('\n');

  const today = new Date().toISOString().slice(0, 10);

  const userMessage =
    `Today: ${today}\n\n` +
    `Transcript:\n"""${transcript}"""\n\n` +
    `Supplies catalog (id | category | name):\n${supplyList}\n\n` +
    `Facilities (id | name):\n${facilityList}`;

  // The proxy answers signed-in company accounts only (item #907, 2026-10-20);
  // the model and the size are fixed on the server.
  const idToken = await firebaseAuth.currentUser?.getIdToken();
  if (!idToken) {
    console.warn('[voice] not signed in; skipping parse');
    return null;
  }

  let res: Response;
  try {
    res = await fetch(PROXY_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
      body: JSON.stringify({
        system: SYSTEM_PROMPT,
        messages: [{ role: 'user', content: userMessage }],
      }),
    });
  } catch (e) {
    console.warn('[voice] proxy network error', e);
    return null;
  }

  if (!res.ok) {
    console.warn('[voice] proxy returned', res.status);
    return null;
  }

  // Defensively unwrap — different proxy implementations shape this differently.
  let raw: string | null = null;
  try {
    const body = await res.json();
    if (typeof body === 'string') {
      raw = body;
    } else if (body?.content?.[0]?.text) {
      raw = body.content[0].text;
    } else if (typeof body?.text === 'string') {
      raw = body.text;
    } else if (typeof body?.message === 'string') {
      raw = body.message;
    } else if (typeof body?.completion === 'string') {
      raw = body.completion;
    }
  } catch (e) {
    console.warn('[voice] proxy JSON parse failed', e);
    return null;
  }

  if (!raw) return null;

  // The model may emit a code fence or trailing prose; pull the first {...} block.
  const match = raw.match(/\{[\s\S]*\}/);
  if (!match) {
    console.warn('[voice] no JSON object in model output', raw);
    return null;
  }

  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const parsed = JSON.parse(match[0]) as any;
    const rawHeader = (parsed?.header ?? {}) as Record<string, unknown>;
    const rawItems = Array.isArray(parsed?.items)
      ? (parsed.items as unknown[])
      : [];
    const items: ParsedSupplyItem[] = rawItems.map((it) => {
      const r = (it ?? {}) as Record<string, unknown>;
      return {
        item: typeof r.item === 'string' ? r.item : null,
        quantity: typeof r.quantity === 'number' ? r.quantity : null,
        unit: typeof r.unit === 'string' ? r.unit.toUpperCase() : null,
        supply_id: typeof r.supply_id === 'string' ? r.supply_id : null,
      };
    });

    return {
      header: {
        facility:
          typeof rawHeader.facility === 'string' ? rawHeader.facility : null,
        facility_id:
          typeof rawHeader.facility_id === 'string'
            ? rawHeader.facility_id
            : null,
        urgency: normalizeUrgency(
          typeof rawHeader.urgency === 'string' ? rawHeader.urgency : null,
        ),
        date_needed:
          typeof rawHeader.date_needed === 'string' &&
          /^\d{4}-\d{2}-\d{2}$/.test(rawHeader.date_needed)
            ? rawHeader.date_needed
            : null,
        notes:
          typeof rawHeader.notes === 'string' && rawHeader.notes.trim()
            ? rawHeader.notes
            : null,
      },
      items,
    };
  } catch (e) {
    console.warn('[voice] JSON.parse failed on model output', e, match[0]);
    return null;
  }
}

function normalizeUrgency(
  v: SupplyUrgency | string | null | undefined,
): SupplyUrgency | null {
  if (!v) return null;
  const s = String(v).toLowerCase();
  if (s.startsWith('em')) return 'Emergency';
  if (s.startsWith('pl') || s.includes('not urgent') || s.includes('no rush'))
    return 'Planned';
  if (s.startsWith('so') || s.startsWith('no')) return 'Soon';
  return null;
}
